import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Star, ExternalLink, ChevronLeft, ChevronRight, Quote, ShieldCheck } from "lucide-react";
import client from "@/lib/api";
import { imgSet } from "@/lib/img";

/**
 * Session 32b · Echte Google-Reviews · vollständig überarbeitet.
 *
 * - Nur ≥ 4-Sterne-Bewertungen (Backend filtert; Frontend zusätzlich).
 * - Reihenfolge wird bei jedem Seitenaufruf leicht gemischt (Fisher-Yates)
 *   → wiederkehrende Besucher sehen frische Reviews.
 * - Skeleton-Loader mit exakt gleicher Höhe wie das finale Widget, kein
 *   Layout-Shift.
 * - Mobile: horizontaler Swipe-Carousel (scroll-snap).
 * - Desktop: 3 Karten parallel (Grid).
 * - Prominenter Aggregate-Header: „★ 5,0 bei Google · XXX Bewertungen ·
 *   Verifiziert" – der ganze Header ist klickbar.
 * - Owner-Antworten werden dezent unter der jeweiligen Rezension eingeblendet.
 * - Design vollständig im Picturebox24-Stil, keine Google-Farben/-Logos.
 *
 * Varianten:
 *  - "full"    → Produktseite / Homepage; Titel + Header + Cards
 *  - "compact" → Wizard-Sidebar / Inline; kompakt, 1 Karte + Auto-Slider
 */
export default function GoogleReviews({ variant = "full", className = "", testIdPrefix = "google-reviews" }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const [ready, setReady] = useState(false);
  const rmRef = useRef(false);
  const resumeTimer = useRef(null);
  const scrollerRef = useRef(null);

  const isCompact = variant === "compact";

  useEffect(() => {
    rmRef.current =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  }, []);

  useEffect(() => {
    let cancelled = false;
    client
      .get("/google-reviews")
      .then((r) => {
        if (cancelled) return;
        if (r.data?.ok) setData(r.data);
        else setError(true);
        setReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Zufallsreihenfolge pro Seitenaufruf (Fisher-Yates). useMemo mit
  // data-Referenz → gleicher Loadscope bekommt gleiche Reihenfolge, neuer
  // Loadscope randomized neu.
  const reviews = useMemo(() => {
    const list = (data?.reviews || []).filter((r) => (r.rating || 0) >= 4);
    const shuffled = list.slice();
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  }, [data]);

  const pauseBriefly = useCallback(() => {
    setPaused(true);
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = setTimeout(() => setPaused(false), 9000);
  }, []);

  // Auto-Rotation nur in compact-Variante (Desktop-Full zeigt mehrere Karten)
  useEffect(() => {
    if (!isCompact || !ready || paused || reviews.length <= 1 || rmRef.current) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % reviews.length), 7000);
    return () => clearInterval(t);
  }, [isCompact, ready, paused, reviews.length]);

  useEffect(() => () => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
  }, []);

  const go = (d) => {
    if (isCompact) {
      setIdx((i) => (i + d + reviews.length) % reviews.length);
      pauseBriefly();
    } else {
      const el = scrollerRef.current;
      if (!el) return;
      const cardWidth = el.querySelector("[data-review-card]")?.offsetWidth || 320;
      el.scrollBy({ left: d * (cardWidth + 20), behavior: "smooth" });
    }
  };

  if (!ready) {
    return <GoogleReviewsSkeleton variant={variant} className={className} testIdPrefix={testIdPrefix} />;
  }
  if (error || !data?.ok || reviews.length === 0) {
    return null;
  }

  const rating = data.rating;
  const total = data.user_ratings_total;
  const googleUrl = data.google_url;

  return (
    <section
      className={`pb24-reviews ${isCompact ? "is-compact" : "is-full"} ${className}`}
      data-testid={testIdPrefix}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onTouchStart={pauseBriefly}
    >
      {!isCompact && (
        <div className="mb-6 sm:mb-8">
          <p className="eyebrow mb-2">
            <span className="gold-line" /> Echte Kundenstimmen
          </p>
          <h2 className="heading-section">Was unsere Kunden sagen</h2>
        </div>
      )}

      <a
        href={googleUrl || "#"}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => {
          if (!googleUrl) e.preventDefault();
        }}
        className="pb24-reviews-hero"
        data-testid={`${testIdPrefix}-hero`}
        aria-label="Alle Google-Bewertungen ansehen"
      >
        <div className="pb24-reviews-hero-left">
          <StarRow value={rating || 5} size="lg" />
          <div className="pb24-reviews-hero-score">
            <span className="pb24-reviews-hero-value">
              {typeof rating === "number" ? rating.toFixed(1).replace(".", ",") : "5,0"}
            </span>
            <span className="pb24-reviews-hero-label">bei Google</span>
          </div>
        </div>
        <div className="pb24-reviews-hero-right">
          <span className="pb24-reviews-hero-count" data-testid={`${testIdPrefix}-total`}>
            {total ? `${total} Bewertungen` : `${reviews.length} Bewertungen`}
          </span>
          <span className="pb24-reviews-hero-verified">
            <ShieldCheck className="h-3.5 w-3.5" aria-hidden="true" />
            Verifizierte Google-Bewertungen
          </span>
        </div>
        {!isCompact && (
          <span className="pb24-reviews-hero-cta" aria-hidden="true">
            Alle ansehen <ExternalLink className="h-3.5 w-3.5" />
          </span>
        )}
      </a>

      {isCompact ? (
        <CompactSlider
          reviews={reviews}
          idx={idx}
          setIdx={setIdx}
          pauseBriefly={pauseBriefly}
          testIdPrefix={testIdPrefix}
        />
      ) : (
        <DesktopScroller
          reviews={reviews}
          scrollerRef={scrollerRef}
          go={go}
          testIdPrefix={testIdPrefix}
        />
      )}
    </section>
  );
}

/* ------------------------ Compact Auto-Slider (Mobile & Wizard) ------------------------ */

function CompactSlider({ reviews, idx, setIdx, pauseBriefly, testIdPrefix }) {
  const current = reviews[idx % reviews.length];
  return (
    <div className="pb24-reviews-compact-wrap" data-testid={`${testIdPrefix}-slider`}>
      <blockquote
        key={idx}
        className="pb24-reviews-card pb24-reviews-fade"
        data-testid={`${testIdPrefix}-review-${idx}`}
      >
        <Quote className="pb24-reviews-quote-mark" aria-hidden="true" />
        <p className="pb24-reviews-text">„{current.text}"</p>
        <ReviewAuthor r={current} testId={`${testIdPrefix}-author-${idx}`} />
        {current.owner_reply && <OwnerReply reply={current.owner_reply} />}
      </blockquote>

      {reviews.length > 1 && (
        <div className="pb24-reviews-controls">
          <button
            type="button"
            onClick={() => {
              setIdx((i) => (i - 1 + reviews.length) % reviews.length);
              pauseBriefly();
            }}
            aria-label="Vorherige Bewertung"
            className="pb24-reviews-nav"
            data-testid={`${testIdPrefix}-prev`}
          >
            <ChevronLeft className="h-4 w-4" />
          </button>
          <div className="pb24-reviews-dots" role="tablist">
            {reviews.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  setIdx(i);
                  pauseBriefly();
                }}
                aria-label={`Bewertung ${i + 1} anzeigen`}
                className={`pb24-reviews-dot ${i === idx ? "is-active" : ""}`}
                data-testid={`${testIdPrefix}-dot-${i}`}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={() => {
              setIdx((i) => (i + 1) % reviews.length);
              pauseBriefly();
            }}
            aria-label="Nächste Bewertung"
            className="pb24-reviews-nav"
            data-testid={`${testIdPrefix}-next`}
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
}

/* ------------------------ Desktop Grid + Mobile Swipe ------------------------ */

function DesktopScroller({ reviews, scrollerRef, go, testIdPrefix }) {
  return (
    <div className="pb24-reviews-scroller-wrap">
      <div className="pb24-reviews-scroller" ref={scrollerRef} data-testid={`${testIdPrefix}-scroller`}>
        {reviews.map((r, i) => (
          <blockquote
            key={i}
            data-review-card
            data-testid={`${testIdPrefix}-review-${i}`}
            className="pb24-reviews-card"
          >
            <Quote className="pb24-reviews-quote-mark" aria-hidden="true" />
            <p className="pb24-reviews-text">„{r.text}"</p>
            <ReviewAuthor r={r} testId={`${testIdPrefix}-author-${i}`} />
            {r.owner_reply && <OwnerReply reply={r.owner_reply} />}
          </blockquote>
        ))}
      </div>
      <div className="pb24-reviews-scroller-controls">
        <button
          type="button"
          onClick={() => go(-1)}
          aria-label="Nach links scrollen"
          className="pb24-reviews-nav"
          data-testid={`${testIdPrefix}-scroll-prev`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        <span className="pb24-reviews-scroller-hint">Weitere Bewertungen zum Wischen</span>
        <button
          type="button"
          onClick={() => go(1)}
          aria-label="Nach rechts scrollen"
          className="pb24-reviews-nav"
          data-testid={`${testIdPrefix}-scroll-next`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}

/* ------------------------ Review-Author + Owner-Reply ------------------------ */

function ReviewAuthor({ r, testId }) {
  return (
    <footer className="pb24-reviews-author" data-testid={testId}>
      {r.author_photo ? (
        <img
          {...imgSet(r.author_photo, "(max-width: 768px) 100vw, 50vw")}
          alt=""
          loading="lazy"
          referrerPolicy="no-referrer"
          className="pb24-reviews-avatar"
        />
      ) : (
        <span className="pb24-reviews-avatar pb24-reviews-avatar-fallback" aria-hidden="true">
          {(r.author_name || "?").trim().charAt(0)}
        </span>
      )}
      <div className="pb24-reviews-author-meta">
        <span className="pb24-reviews-author-name">{r.author_name || "Google-Kunde"}</span>
        <span className="pb24-reviews-author-time">
          <StarRow value={r.rating} size="sm" />
          {r.relative_time ? <span>· {r.relative_time}</span> : null}
        </span>
      </div>
    </footer>
  );
}

function OwnerReply({ reply }) {
  if (!reply?.text) return null;
  return (
    <div className="pb24-reviews-owner-reply">
      <span className="pb24-reviews-owner-reply-label">
        <span className="gold-line" /> Antwort von Picturebox24
      </span>
      <p className="pb24-reviews-owner-reply-text">{reply.text}</p>
      {reply.relative_time && (
        <span className="pb24-reviews-owner-reply-time">{reply.relative_time}</span>
      )}
    </div>
  );
}

/* ------------------------ Stars ------------------------ */

function StarRow({ value = 0, size = "md" }) {
  const rounded = Math.round(value * 2) / 2;
  const cls = size === "lg" ? "h-5 w-5" : size === "sm" ? "h-3 w-3" : "h-4 w-4";
  return (
    <span className="pb24-reviews-stars" aria-label={`${value} von 5 Sternen`}>
      {[1, 2, 3, 4, 5].map((s) => {
        const filled = rounded >= s;
        return (
          <Star
            key={s}
            className={`${cls} ${filled ? "pb24-star-full" : "pb24-star-empty"}`}
            fill={filled ? "currentColor" : "none"}
            strokeWidth={1.6}
          />
        );
      })}
    </span>
  );
}

/* ------------------------ Skeleton ------------------------ */

function GoogleReviewsSkeleton({ variant, className, testIdPrefix }) {
  const isCompact = variant === "compact";
  return (
    <section
      className={`pb24-reviews pb24-reviews-skeleton-wrap ${isCompact ? "is-compact" : "is-full"} ${className}`}
      data-testid={`${testIdPrefix}-loading`}
      aria-hidden="true"
    >
      {!isCompact && (
        <div className="mb-6 sm:mb-8">
          <div className="pb24-skel pb24-skel-line" style={{ width: "160px", height: "12px" }} />
          <div className="pb24-skel pb24-skel-line" style={{ width: "260px", height: "22px", marginTop: "10px" }} />
        </div>
      )}
      <div className="pb24-reviews-hero pb24-reviews-hero-skeleton">
        <div className="pb24-skel pb24-skel-line" style={{ width: "120px", height: "22px" }} />
        <div className="pb24-skel pb24-skel-line" style={{ width: "160px", height: "16px" }} />
      </div>
      <div className={`pb24-reviews-scroller ${isCompact ? "is-compact-skeleton" : ""}`}>
        {Array.from({ length: isCompact ? 1 : 3 }).map((_, i) => (
          <div key={i} className="pb24-reviews-card pb24-reviews-card-skeleton">
            <div className="pb24-skel pb24-skel-line" style={{ width: "80%", height: "12px" }} />
            <div className="pb24-skel pb24-skel-line" style={{ width: "95%", height: "12px", marginTop: "8px" }} />
            <div className="pb24-skel pb24-skel-line" style={{ width: "70%", height: "12px", marginTop: "8px" }} />
            <div className="pb24-skel pb24-skel-line" style={{ width: "40%", height: "12px", marginTop: "24px" }} />
          </div>
        ))}
      </div>
    </section>
  );
}
